<script lang="ts">
  // TrucoActions — bot�es inline (Aceitar / Pedir N / Correr) que
  // aparecem na action-bar quando o time oposto fez um pedido de
  // truco e cabe a mim/parceiro responder. Substitui o modal antigo
  // (request Leonardo 2026-05-18: "n�o quero popup cobrindo a mesa").
  //
  // Quando o user clica, dispara um speak() local pro pr�prio retrato
  // ja mostrar o bal�o "ACEITO!"/"CORRO!" sem esperar o snapshot diff.
  import { onDestroy } from 'svelte';
  import type { MatchSnapshot } from '../../stores/match';
  import {
    pendingValueAfterAccept,
    nextEscalationValue,
    escalationName,
  } from '../../lib/cards';
  import { send } from '../../stores/ws';
  import { userId } from '../../stores/auth';
  import { speak } from '../../stores/speech';
  import { markMyRunDecision } from '../../stores/narration';

  type Props = { snap: MatchSnapshot };
  let { snap }: Props = $props();

  // Countdown durante truco pending — antes nao mostravamos timer
  // (Leonardo 2026-05-19: "qnd alguem pede truco n aparece o timer").
  // Servidor manda snap.deadline (ISO timestamp do auto-refuse) e
  // snap.turn_duration_sec. Re-renderiza a cada 250ms enquanto
  // truco_challenge_running.
  let now = $state(Date.now());
  let tickId: number | null = null;
  $effect(() => {
    if (snap.truco_challenge_running && snap.deadline) {
      if (tickId === null) {
        tickId = window.setInterval(() => (now = Date.now()), 250);
      }
    } else if (tickId !== null) {
      clearInterval(tickId);
      tickId = null;
    }
  });
  onDestroy(() => {
    if (tickId !== null) clearInterval(tickId);
  });
  let deadlineMs = $derived(snap.deadline ? new Date(snap.deadline).getTime() : 0);
  let remainingSec = $derived(
    deadlineMs ? Math.max(0, Math.ceil((deadlineMs - now) / 1000)) : 0,
  );
  let danger = $derived(remainingSec > 0 && remainingSec <= 5);

  let mineIds = $derived.by((): string[] => {
    if (snap.format === '2v2') {
      const a = (snap.team_a || []).map((p) => p.id);
      const b = (snap.team_b || []).map((p) => p.id);
      return a.includes($userId) ? a : b.includes($userId) ? b : [];
    }
    return [$userId];
  });

  let pendingAgainstMe = $derived(
    snap.truco_challenge_running &&
      !!snap.truco_challenger_id &&
      !mineIds.includes(snap.truco_challenger_id),
  );

  let pendingValue = $derived(pendingValueAfterAccept(snap.round_points || 1));
  let raiseValue = $derived(nextEscalationValue(pendingValue));
  let canRaise = $derived(raiseValue > pendingValue && !snap.truco_locked);

  function accept() {
    // ACEITO so na minha cabeca (1). Felipe 2026-06-01: 2 cabecas so no correr.
    speak($userId, 'ACEITO!', 'ack');
    send('game:truco:accept');
  }
  function refuse() {
    // CORRER: balao nos DOIS do time (o time desiste junto).
    for (const id of mineIds.length ? mineIds : [$userId]) speak(id, 'TÔ FORA!', 'run');
    markMyRunDecision();
    send('game:truco:refuse');
  }
  function raise() {
    // Raise vai aparecer com balao sticky via narration.ts assim
    // que o snap voltar. Aqui dou o feedback instantaneo local
    // tambem sticky pra nao piscar entre o local e o do snap.
    const word = raiseValue === 6 ? 'SEIS' : raiseValue === 9 ? 'NOVE' : raiseValue === 12 ? 'DOZE' : 'TRUCO';
    speak($userId, `${word}!`, 'shout', { sticky: true });
    send('game:truco:raise');
  }
</script>

{#if pendingAgainstMe && snap.status === 'running'}
  <div class="truco-actions">
    {#if remainingSec > 0}
      <div class="countdown" class:danger title="tempo restante pra responder">
        {remainingSec}s
      </div>
    {/if}
    <button class="act accept" onclick={accept}>
      <span class="lbl">aceitar</span>
      <span class="val">{pendingValue}</span>
    </button>
    {#if canRaise}
      <button class="act raise" onclick={raise}>
        <span class="lbl">{escalationName(raiseValue)}</span>
      </button>
    {/if}
    <button class="act refuse" onclick={refuse}>
      <span class="lbl">correr</span>
    </button>
  </div>
{/if}

<style>
  .truco-actions {
    display: flex;
    flex: 1 1 auto;
    gap: 6px;
    align-items: stretch;
    justify-content: flex-end;
    flex-wrap: nowrap;
    animation: slide-up 0.22s cubic-bezier(0.2, 0.9, 0.3, 1.2);
  }
  @keyframes slide-up {
    from { transform: translateY(8px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
  .act {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 14px 12px;
    border-radius: var(--radius-md);
    border: 1px solid var(--border);
    background: var(--surface);
    color: var(--text);
    font-family: inherit;
    font-weight: 800;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    transition: background 0.15s, border-color 0.15s, transform 0.1s;
    flex: 1 1 0;
    min-width: 0;
    white-space: nowrap;
  }
  .act:active { transform: scale(0.97); }
  .act .lbl { font-size: 13px; line-height: 1; }
  .act .val { font-size: 15px; font-weight: 900; line-height: 1; }

  /* Tela estreita: corta padding e gap ainda mais. */
  @media (max-width: 520px) {
    .truco-actions { gap: 5px; }
    .act { padding: 13px 8px; }
    .act .lbl { font-size: 12px; }
    .act .val { font-size: 14px; }
  }

  .act.accept {
    border-color: rgba(108, 255, 189, 0.55);
    background: rgba(108, 255, 189, 0.1);
    color: var(--ok);
  }
  .act.accept:hover { background: rgba(108, 255, 189, 0.2); }
  .act.raise {
    border-color: rgba(255, 210, 74, 0.55);
    background: rgba(255, 210, 74, 0.1);
    color: var(--warn);
  }
  .act.raise:hover { background: rgba(255, 210, 74, 0.2); }
  .act.refuse {
    border-color: rgba(255, 76, 126, 0.55);
    background: rgba(255, 76, 126, 0.1);
    color: var(--danger);
  }
  .act.refuse:hover { background: rgba(255, 76, 126, 0.2); }

  .countdown {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 38px;
    padding: 6px 8px;
    border-radius: var(--radius-md);
    border: 1px solid rgba(180, 210, 255, 0.35);
    background: rgba(2, 3, 26, 0.6);
    color: rgba(180, 210, 255, 0.95);
    font-family: ui-monospace, "SFMono-Regular", "Menlo", monospace;
    font-weight: 800;
    font-size: 13px;
    letter-spacing: 0.5px;
    flex: 0 0 auto;
    transition: color 0.2s, border-color 0.2s, box-shadow 0.2s;
  }
  .countdown.danger {
    color: var(--danger);
    border-color: rgba(255, 76, 126, 0.7);
    box-shadow: 0 0 10px rgba(255, 76, 126, 0.45);
    animation: pulse-danger 0.6s ease-in-out infinite;
  }
  @keyframes pulse-danger {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.08); }
  }
  @media (max-width: 520px) {
    .countdown {
      min-width: 32px;
      padding: 5px 6px;
      font-size: 11px;
    }
  }
</style>
